// Update the OptionsManager component in QuestionModal.js

import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Table, InputGroup } from 'react-bootstrap';

export function OptionsManager({ options = [], onChange, errors }) {
  const [localOptions, setLocalOptions] = useState(options);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [advancedMode, setAdvancedMode] = useState(false);

  // Sync with parent when options prop changes
  useEffect(() => {
    setLocalOptions(options);
    // Auto-enable advanced mode if any option has custom value (value !== auto-generated)
    const hasCustomValues = options.some(opt => 
      opt.value !== textToValue(opt.label)
    );
    if (hasCustomValues) {
      setAdvancedMode(true);
    }
  }, [options]);

  // Convert any text to a safe value
  const textToValue = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '')
      || 'option';
  };

  const addOption = () => {
    if (newOptionLabel.trim()) {
      const label = newOptionLabel.trim();
      const value = textToValue(label);
      
      // Check for duplicate values
      const existingValues = localOptions.map(opt => opt.value);
      let finalValue = value;
      let counter = 1;
      
      while (existingValues.includes(finalValue)) {
        finalValue = `${value}_${counter}`;
        counter++;
      }
      
      const updatedOptions = [...localOptions, { 
        label, 
        value: finalValue, 
        id: Date.now() 
      }];
      
      setLocalOptions(updatedOptions);
      onChange(updatedOptions);
      setNewOptionLabel('');
    }
  };

  const removeOption = (index) => {
    const updatedOptions = localOptions.filter((_, i) => i !== index);
    setLocalOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const updateOptionLabel = (index, newLabel) => {
    if (!newLabel.trim()) return;
    
    const updatedOptions = [...localOptions];
    updatedOptions[index] = { 
      ...updatedOptions[index], 
      label: newLabel.trim()
    };
    
    // In simple mode, auto-update value based on label
    if (!advancedMode) {
      const autoValue = textToValue(newLabel);
      const existingValues = updatedOptions
        .filter((_, i) => i !== index)
        .map(opt => opt.value);
      
      let finalValue = autoValue;
      let counter = 1;
      
      while (existingValues.includes(finalValue)) {
        finalValue = `${autoValue}_${counter}`;
        counter++;
      }
      
      updatedOptions[index].value = finalValue;
    }
    
    setLocalOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const updateOptionValue = (index, newValue) => {
    if (!newValue.trim()) return;
    
    const cleanValue = newValue.trim().replace(/[^a-z0-9_]/gi, '_');
    
    const updatedOptions = [...localOptions];
    updatedOptions[index] = { 
      ...updatedOptions[index], 
      value: cleanValue
    };
    
    setLocalOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const toggleAdvancedMode = () => {
    setAdvancedMode(!advancedMode);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption();
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Form.Label>
          Dropdown Options 
          <small className="text-muted ms-2">({localOptions.length} options)</small>
        </Form.Label>
        
        {localOptions.length > 0 && (
          <Button 
            variant={advancedMode ? "primary" : "outline-secondary"} 
            size="sm"
            onClick={toggleAdvancedMode}
          >
            {advancedMode ? "🔧 Advanced" : "⚙️ Edit Values"}
          </Button>
        )}
      </div>
      
      {/* Existing Options */}
      {localOptions.length > 0 && (
        <Card className="mb-3">
          <Card.Body className="p-0">
            {advancedMode ? (
              // Advanced Mode: Table with label and value columns
              <Table className="mb-0" size="sm">
                <thead className="table-light">
                  <tr>
                    <th width="45%">Label (what users see)</th>
                    <th width="45%">Value (sent to AI)</th>
                    <th width="10%">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {localOptions.map((option, index) => (
                    <tr key={option.id || index}>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={option.label || ''}
                          onChange={(e) => updateOptionLabel(index, e.target.value)}
                          placeholder="Display text"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={option.value || ''}
                          onChange={(e) => updateOptionValue(index, e.target.value)}
                          placeholder="Value for AI"
                          className="font-monospace"
                        />
                      </td>
                      <td>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => removeOption(index)}
                          title="Remove option"
                        >
                          ✕
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              // Simple Mode: Just labels
              <div className="p-3">
                {localOptions.map((option, index) => (
                  <div key={option.id || index} className="d-flex align-items-center mb-2">
                    <div className="flex-grow-1 me-2">
                      <Form.Control
                        size="sm"
                        type="text"
                        value={option.label || ''}
                        onChange={(e) => updateOptionLabel(index, e.target.value)}
                        placeholder="Option text"
                      />
                      <small className="text-muted">
                        → <code>{option.value}</code>
                      </small>
                    </div>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => removeOption(index)}
                      title="Remove option"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Add New Option */}
      <Card className="border-2 border-dashed">
        <Card.Body className="p-3">
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              size="sm"
              type="text"
              placeholder="Add option text (e.g., Happy, Sad, Excited...)"
              value={newOptionLabel}
              onChange={(e) => setNewOptionLabel(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-grow-1"
            />
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={addOption}
              disabled={!newOptionLabel.trim()}
            >
              Add
            </Button>
          </div>
          
          {/* Live preview */}
          {newOptionLabel.trim() && (
            <small className="text-muted">
              Will create: <strong>{newOptionLabel.trim()}</strong> 
              {!advancedMode && (
                <span> → <code>{textToValue(newOptionLabel)}</code></span>
              )}
            </small>
          )}
          
          <div className="mt-2">
            <small className="text-muted">
              {advancedMode ? (
                <>🔧 <strong>Advanced mode:</strong> You can customize both labels and values. Values are sent to the AI.</>
              ) : (
                <>⚡ <strong>Simple mode:</strong> Values are auto-generated from labels. Click "Edit Values" for custom values.</>
              )}
            </small>
          </div>
        </Card.Body>
      </Card>

      {errors && (
        <div className="text-danger mt-2">
          <small>{errors}</small>
        </div>
      )}
      
      {/* Help text */}
      <Form.Text className="text-muted mt-2">
        <strong>Labels</strong> are what users see in the dropdown. 
        <strong>Values</strong> are what gets sent to the AI in your prompt.
        {!advancedMode && " Switch to advanced mode to customize values separately."}
      </Form.Text>
    </div>
  );
}